import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import SiteHeader from "@/components/SiteHeader";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { FileDown, Printer, Lock, CheckCircle2, Circle, FileText, Gauge, Calculator, MessageSquare, Sparkles, Route, Mail } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useProgress } from "@/hooks/useProgress";
import { PLAYBOOK_STEPS, PROGRAM_NAME } from "@/data/playbook";
import { STRATEGIST } from "@/data/strategist";
import StrategyCallDialog from "@/components/StrategyCallDialog";
import { gatherReportData, generateReportPdf } from "@/lib/exportReport";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EQ = { dry_van: "Dry Van", reefer: "Reefer", flatbed: "Flatbed", step_deck: "Step Deck", box_truck: "Box Truck", power_only: "Power Only" };
const perMile = (n) => `$${(isFinite(n) ? n : 0).toFixed(3)}`;
const money = (n) => `$${(isFinite(n) ? n : 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

const readJSON = (k) => { try { return JSON.parse(localStorage.getItem(k)); } catch { return null; } };

export default function ExportHub() {
  const { user } = useAuth();
  const { count } = useProgress();
  const isPaid = user?.subscription_active === true;
  const [coach, setCoach] = useState([]);
  const [upsell, setUpsell] = useState(false);

  const diagnostic = useMemo(() => readJSON("routehaul_diagnostic_v1"), []);
  const cpm = useMemo(() => readJSON("routehaul_cpm_v1"), []);

  useEffect(() => {
    if (isPaid) {
      axios.get(`${API}/coach/history`).then(({ data }) => setCoach(data || [])).catch(() => {});
    }
  }, [isPaid]);

  const guard = (fn) => () => {
    if (!isPaid) { setUpsell(true); return; }
    fn();
  };

  const doPdf = () => {
    try {
      generateReportPdf(gatherReportData(coach));
      toast.success("Report downloaded as PDF.");
    } catch (e) {
      toast.error("Could not generate the PDF. Please try again.");
    }
  };
  const doPrint = () => window.print();

  const SectionCard = ({ icon: Icon, title, complete, children, cta }) => (
    <div className="border border-slate-200 rounded-md p-6 bg-white break-inside-avoid">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Icon className="w-4 h-4 text-brand" />
          <h3 className="font-heading text-lg font-semibold text-slate-900">{title}</h3>
        </div>
        {complete === false && cta && (
          <Link to={cta.to} className="no-print text-xs font-bold text-brand hover:underline">{cta.label}</Link>
        )}
      </div>
      {children}
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="no-print"><SiteHeader /></div>

      {/* Intro / actions */}
      <section className="no-print max-w-5xl mx-auto px-6 pt-14 pb-6">
        <div className="text-xs tracking-[0.2em] uppercase font-bold text-brand mb-3">Export Hub</div>
        <h1 className="font-heading text-4xl sm:text-5xl tracking-tighter font-black text-slate-900">Print & export your worksheets</h1>
        <p className="mt-4 text-slate-600 max-w-2xl leading-relaxed">
          Everything you've built in the app — your 12-Step progress, readiness diagnostic, CPM worksheet and AI Coach notes — combined into one clean report. Print it on your portable printer or save the PDF to your phone.
        </p>
        <div className="mt-8 flex flex-wrap items-center gap-4">
          <Button data-testid="export-pdf-button" onClick={guard(doPdf)} className="h-13 px-7 rounded-full bg-brand hover:bg-brand-hover text-white text-base font-bold py-3">
            {!isPaid && <Lock className="w-4 h-4 mr-2" />}
            <FileDown className="w-5 h-5 mr-2" /> Download PDF
          </Button>
          <Button data-testid="export-print-button" onClick={guard(doPrint)} variant="outline" className="h-13 px-7 rounded-full border-slate-300 text-slate-900 text-base font-bold py-3">
            {!isPaid && <Lock className="w-4 h-4 mr-2" />}
            <Printer className="w-5 h-5 mr-2" /> Print
          </Button>
          {!isPaid && (
            <span data-testid="export-locked-note" className="text-sm text-slate-500 inline-flex items-center gap-1.5">
              <Lock className="w-3.5 h-3.5" /> Exporting is a paid feature — preview below is free.
            </span>
          )}
        </div>
      </section>

      {/* Report preview / print area */}
      <section className="print-area max-w-5xl mx-auto px-6 pb-24 space-y-5">
        {/* Print-only report header */}
        <div className="hidden print:block mb-6">
          <h2 className="font-heading text-2xl font-black text-slate-900">RouteHaul Operator Report</h2>
          <p className="text-sm text-slate-500">{PROGRAM_NAME} · Generated {new Date().toLocaleString()}</p>
        </div>

        <SectionCard icon={FileText} title={`12-Step Business Foundation Guide — ${count}/12 complete`}>
          <div className="grid sm:grid-cols-2 gap-x-8 gap-y-2">
            {PLAYBOOK_STEPS.map((s) => {
              const done = (readJSON("routehaul_progress_v1") || []).includes(s.n);
              return (
                <div key={s.n} className="flex items-start gap-2 text-sm">
                  {done ? <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" /> : <Circle className="w-4 h-4 text-slate-300 shrink-0 mt-0.5" />}
                  <span className={done ? "text-slate-900" : "text-slate-500"}>{s.n}. {s.title}</span>
                </div>
              );
            })}
          </div>
        </SectionCard>

        <SectionCard icon={Gauge} title="Readiness Diagnostic" complete={!!diagnostic} cta={{ to: "/diagnostic", label: "Run diagnostic" }}>
          {diagnostic ? (
            <div className="space-y-3 text-sm text-slate-700">
              <div className="flex items-baseline gap-3">
                <span className="font-mono font-black text-3xl text-slate-900 tabular">{diagnostic.score}<span className="text-base text-slate-400">/100</span></span>
                <span className="text-xs uppercase font-bold tracking-wide text-slate-500">tier: {diagnostic.tier}</span>
              </div>
              {diagnostic.ai_analysis?.headline && <p className="font-bold text-brand">{diagnostic.ai_analysis.headline}</p>}
              {diagnostic.ai_analysis?.summary && <p className="text-slate-600 leading-relaxed">{diagnostic.ai_analysis.summary}</p>}
              <div className="grid sm:grid-cols-2 gap-4 pt-2">
                {!!diagnostic.ai_analysis?.strengths?.length && (
                  <div><p className="font-bold text-slate-900 mb-1">Strengths</p><ul className="list-disc pl-5 space-y-1 text-emerald-700">{diagnostic.ai_analysis.strengths.map((x, i) => <li key={i}>{x}</li>)}</ul></div>
                )}
                {!!diagnostic.ai_analysis?.gaps?.length && (
                  <div><p className="font-bold text-slate-900 mb-1">Gaps to close</p><ul className="list-disc pl-5 space-y-1 text-amber-700">{diagnostic.ai_analysis.gaps.map((x, i) => <li key={i}>{x}</li>)}</ul></div>
                )}
              </div>
            </div>
          ) : (
            <p className="text-sm text-slate-400">No diagnostic completed yet. Run the 2-minute readiness diagnostic to include your score.</p>
          )}
        </SectionCard>

        <SectionCard icon={Calculator} title="Cost-Per-Mile Worksheet" complete={!!cpm?.results} cta={{ to: "/cpm", label: "Open calculator" }}>
          {cpm?.results ? (
            <div className="space-y-3 text-sm">
              <p className="text-xs uppercase font-bold tracking-wide text-slate-500">Equipment: {EQ[cpm.equipment] || cpm.equipment}</p>
              <div className="grid sm:grid-cols-2 gap-3">
                <Stat label="Break-even cost / mile" value={perMile(cpm.results.totalCPM)} accent="brand" />
                <Stat label={`Suggested rate (+${cpm.inputs?.margin}% margin)`} value={perMile(cpm.results.suggestedRate)} accent="emerald" />
                <Stat label="Fixed cost / mile" value={perMile(cpm.results.fixedCPM)} />
                <Stat label="Fuel cost / mile" value={perMile(cpm.results.fuelCPM)} />
                <Stat label="Fixed costs / month" value={money(cpm.results.fixedMonthly)} />
                <Stat label="Total operating cost / month" value={money(cpm.results.monthlyOperating)} />
              </div>
            </div>
          ) : (
            <p className="text-sm text-slate-400">No CPM worksheet saved yet. Open the CPM Calculator and enter your numbers.</p>
          )}
        </SectionCard>

        <SectionCard icon={MessageSquare} title="AI Coach Notes" complete={isPaid ? coach.length > 0 : undefined}>
          {!isPaid ? (
            <p className="text-sm text-slate-400 inline-flex items-center gap-1.5"><Sparkles className="w-4 h-4 text-brand" /> Your AI Coach conversation is included in the export on the paid tier.</p>
          ) : coach.length ? (
            <div className="space-y-3 max-h-72 overflow-y-auto print:max-h-none print:overflow-visible">
              {coach.map((m, i) => (
                <div key={i} className="text-sm">
                  <span className={`font-bold ${m.role === "user" ? "text-slate-900" : "text-brand"}`}>{m.role === "user" ? "You: " : "Coach: "}</span>
                  <span className="text-slate-600 whitespace-pre-wrap">{m.content}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-slate-400">No AI Coach conversation yet.</p>
          )}
        </SectionCard>

        <SectionCard icon={Route} title="Work With a Lane Strategist">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4 justify-between">
            <div>
              <p className="font-bold text-slate-900">{STRATEGIST.name} — {STRATEGIST.title}</p>
              <p className="text-sm text-slate-500">{STRATEGIST.business}</p>
              <a href={`mailto:${STRATEGIST.email}`} className="inline-flex items-center gap-1.5 text-sm font-bold text-brand hover:underline mt-1">
                <Mail className="w-4 h-4" /> {STRATEGIST.email}
              </a>
              <p className="text-sm text-slate-600 mt-2 max-w-md">{STRATEGIST.blurb}</p>
            </div>
            <div className="no-print shrink-0">
              <StrategyCallDialog source="strategy_export" triggerClassName="h-11 px-6 rounded-full bg-brand hover:bg-brand-hover text-white text-sm font-bold" />
            </div>
          </div>
        </SectionCard>

        <p className="hidden print:block text-xs text-slate-400 pt-4">{PROGRAM_NAME} · Estimates & educational guidance only — verify with FMCSA or a professional.</p>
      </section>
      <Dialog open={upsell} onOpenChange={setUpsell}>
        <DialogContent data-testid="export-upsell-dialog">
          <DialogHeader>
            <div className="w-12 h-12 rounded-md bg-orange-50 flex items-center justify-center mb-2">
              <Lock className="w-6 h-6 text-brand" />
            </div>
            <DialogTitle className="font-heading text-2xl font-black tracking-tight">Exporting is a paid feature</DialogTitle>
            <DialogDescription className="text-slate-600 leading-relaxed">
              Printing and PDF export of your combined operator report is included with the paid tier ($49/mo or $39/yr) — along with the CPM Calculator, Load Board and AI Coach. Upgrade to take your worksheets on the road.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" className="rounded-full font-bold" onClick={() => setUpsell(false)} data-testid="export-upsell-cancel">Maybe later</Button>
            <Link to={user ? "/subscribe" : "/register"}>
              <Button className="rounded-full bg-brand hover:bg-brand-hover text-white font-bold" data-testid="export-upsell-upgrade">Upgrade to export</Button>
            </Link>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Stat = ({ label, value, accent }) => (
  <div className="border border-slate-100 rounded-md p-3 bg-slate-50/60">
    <div className="text-xs text-slate-500">{label}</div>
    <div className={`font-mono font-bold tabular text-lg ${accent === "brand" ? "text-brand" : accent === "emerald" ? "text-emerald-600" : "text-slate-900"}`}>{value}</div>
  </div>
);
